import React, { useEffect, useRef, useState } from 'react';
import './ScrollingSVG.css';

interface ScrollingSVGProps {
  src: string;
  alt: string;
  position: 'left' | 'right';
  verticalOffset?: number;
}

const ScrollingSVG: React.FC<ScrollingSVGProps> = ({ src, alt, position, verticalOffset = 0 }) => {
  const svgRef = useRef<HTMLDivElement>(null);
  const objectRef = useRef<HTMLObjectElement>(null);
  const [opacity, setOpacity] = useState(0);

  // Enable SVGator animation looping
  useEffect(() => {
    const objectElement = objectRef.current;
    if (!objectElement) return;

    const handleLoad = () => {
      try {
        const svgDoc = objectElement.contentDocument;
        if (!svgDoc) return;

        const svgElement = svgDoc.querySelector('svg');
        if (!svgElement || !(svgElement as any).svgatorPlayer) return;

        const player = (svgElement as any).svgatorPlayer;
        
        // Wait for player to be ready and enable looping
        player.ready(() => {
          // Restart animation when it ends
          const restartAnimation = () => {
            if (player.restart) {
              player.restart();
            } else if (player.seek) {
              player.seek(0);
              player.play?.();
            }
          };

          // Set up loop by listening to animation end
          if (player.onanimationend !== undefined) {
            player.onanimationend = restartAnimation;
          } else {
            // Fallback: restart after estimated duration
            const checkAndRestart = () => {
              setTimeout(() => {
                restartAnimation();
                checkAndRestart();
              }, 30000); // 30 seconds, adjust based on your animation duration
            };
            checkAndRestart();
          }
        });
      } catch (error) {
        console.log('Could not access SVG for animation loop:', error);
      }
    };

    objectElement.addEventListener('load', handleLoad);
    return () => {
      objectElement.removeEventListener('load', handleLoad);
    };
  }, []);

  useEffect(() => {
    const updateOpacity = () => {
      if (!svgRef.current) return;

      const viewportCenter = window.innerHeight / 2;
      const rect = svgRef.current.getBoundingClientRect();
      const elementCenter = rect.top + rect.height / 2;
      const distanceFromCenter = Math.abs(elementCenter - viewportCenter);
      
      // Calculate opacity based on how close the SVG is to viewport center
      // Maximum opacity (100%) when SVG is at center, fading to 0 as distance increases
      // Fade zone: 0 to 600px from center (starts earlier, ends later)
      const fadeZone = 600;
      const peakZone = 100; // Zone where opacity stays at 100%
      const maxOpacity = 2;
      
      if (distanceFromCenter > fadeZone) {
        setOpacity(0);
        return;
      }
      
      // If within peak zone, stay at 100% opacity
      if (distanceFromCenter <= peakZone) {
        setOpacity(maxOpacity);
        return;
      }
      
      // Fade in/out outside peak zone with smooth easing
      const fadeRange = fadeZone - peakZone;
      const distanceFromPeak = distanceFromCenter - peakZone;
      const normalizedDistance = distanceFromPeak / fadeRange;
      // Use smoother easing for gradual fade
      const easedDistance = normalizedDistance * normalizedDistance;
      const newOpacity = maxOpacity * (1 - easedDistance);
      setOpacity(Math.max(0, newOpacity));
    };

    const handleScroll = () => {
      requestAnimationFrame(updateOpacity);
    };

    // Initial calculation
    updateOpacity();

    // Add scroll listener
    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', handleScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleScroll);
    };
  }, []);

  return (
    <div className="scrolling-svg-container">
      <div
        ref={svgRef}
        className={`scrolling-svg scrolling-svg-${position}`}
        style={{ 
          opacity,
          transform: verticalOffset !== 0 ? `translateY(${verticalOffset}%)` : undefined
        }}
      >
        <object
          ref={objectRef}
          type="image/svg+xml"
          data={src}
          className="scrolling-svg-image"
          aria-label={alt}
        >
          {alt}
        </object>
      </div>
    </div>
  );
};

export default ScrollingSVG;

